import React, { useEffect, useRef, useState } from "react";
import { Printer, RefreshCw } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { SHORTS, url, canvasOf } from "./QrSection";

const esc = (s = "") => String(s).replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch]));

const buildFlyer = (c, products) => {
  const h = c.hero || {};
  const top = products.filter((p) => p.in_stock !== false && p.price).slice(0, 6);
  const prices = top.map((p) => `<div class="p"><span>${esc(p.name)}</span><b>up to $${p.price}</b></div>`).join("");
  const qrs = SHORTS.map((s) => {
    const cv = canvasOf(s.key);
    return `<div class="q"><img src="${cv ? cv.toDataURL("image/png") : ""}"/><div class="ql" style="color:${s.color}">${s.label}</div><div class="qu">${url(s.key).replace("https://", "")}</div></div>`;
  }).join("");
  return `<!doctype html><html><head><title>StripWise flyer</title><style>
@page{size:letter;margin:0}*{box-sizing:border-box}body{margin:0;font-family:Arial,Helvetica,sans-serif;color:#0f2e2a;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.f{width:8.5in;height:11in;padding:.5in;display:flex;flex-direction:column;overflow:hidden}
.top{display:flex;align-items:center;justify-content:space-between}.top img{height:52px}.tag{background:#e6f8f3;color:#0b6f65;font-weight:bold;padding:6px 14px;border-radius:99px;font-size:14px}
h1{font-size:56px;line-height:1.05;margin:26px 0 10px}h1 em{font-style:normal;color:#0e8c7f}
.sub{font-size:20px;line-height:1.4;color:#3d504d;margin:0 0 20px}
.steps{display:flex;gap:10px;margin-bottom:22px}.steps div{flex:1;background:#0f2e2a;color:#fff;border-radius:12px;padding:16px;font-size:17px;font-weight:bold}.steps span{display:block;color:#7fe0cf;font-size:12px}
.pr{border:2px solid #cfe3df;border-radius:16px;padding:14px 18px;margin-bottom:26px}.pr h2{margin:0 0 8px;font-size:22px;color:#0e8c7f}
.p{display:flex;justify-content:space-between;border-bottom:1px dashed #dcebe8;padding:10px 0;font-size:19px}.p:last-child{border:0}.p b{color:#0b6f65}
.qs{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin-top:auto}.q{text-align:center;border:2px dashed #cfe3df;border-radius:12px;padding:8px 4px}
.q img{width:100%;max-width:140px}.ql{font-weight:bold;font-size:16px}.qu{font-size:10.5px;font-weight:bold}
.ft{margin-top:14px;display:flex;justify-content:space-between;font-size:15px;font-weight:bold}.ft span{color:#0e8c7f}
</style></head><body><div class="f">
<div class="top"><img src="${window.location.origin}/logo.png"/><div class="tag">${esc(c.tagline || "Free quotes for sealed supplies")}</div></div>
<h1>${esc(h.title || "Give Your Extra Sealed Supplies")} <em>${esc(h.highlight || "")}</em></h1>
<p class="sub">${esc(h.subtitle || "")}</p>
<div class="steps"><div><span>STEP 1</span>Get a free quote</div><div><span>STEP 2</span>Ship free, prepaid label</div><div><span>STEP 3</span>${esc(h.payout_text ? "Get paid: " + h.payout_text : "Get paid your way")}</div></div>
${prices ? `<div class="pr"><h2>We pay top dollar for sealed, unexpired supplies</h2>${prices}</div>` : ""}
<div class="qs">${qrs}</div>
<div class="ft"><div>Call or text <span>${esc(c.phone_display || "")}</span></div><div><span>stripwisepays.com</span></div></div>
</div></body></html>`;
};

export const FlyerSection = () => {
  const { rawContent, products } = useSite();
  const [html, setHtml] = useState("");
  const frame = useRef(null);
  const box = useRef(null);
  const [scale, setScale] = useState(0.5);
  useEffect(() => {
    const fit = () => box.current && setScale(box.current.clientWidth / 816);
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);
  const make = () => rawContent && setHtml(buildFlyer(rawContent, products || []));
  useEffect(() => { const t = setTimeout(make, 400); return () => clearTimeout(t); }, [rawContent, products]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="rounded-2xl border border-[#e3ecea] bg-white p-4 md:p-5" data-testid="flyer-section">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex-1">
          <h3 className="text-[17px] font-bold text-[#0f2e2a]">Printable flyer</h3>
          <p className="text-[12px] text-[#6b7c79]">US Letter. Uses your headline, top prices and all 5 QR codes. Prices update when you change them in the admin.</p>
        </div>
        <button onClick={make} className="inline-flex items-center gap-1.5 rounded-full border border-[#cfe3df] px-4 py-2 text-[13px] font-bold text-[#0e8c7f]" data-testid="flyer-refresh"><RefreshCw size={14} />Refresh</button>
        <button onClick={() => frame.current?.contentWindow?.print()} disabled={!html} className="inline-flex items-center gap-1.5 rounded-full bg-[#0e8c7f] px-4 py-2 text-[13px] font-bold text-white hover:bg-[#0b6f65] disabled:opacity-50" data-testid="flyer-print"><Printer size={14} />Print / Save PDF</button>
      </div>
      <div ref={box} className="mx-auto overflow-hidden rounded-lg border border-[#e3ecea] shadow-sm" style={{ width: "min(100%, 408px)", aspectRatio: "8.5 / 11" }}>
        <div className="origin-top-left" style={{ width: 816, height: 1056, transform: `scale(${scale})` }}>
          <iframe ref={frame} title="Flyer preview" srcDoc={html} className="h-full w-full border-0" data-testid="flyer-preview" />
        </div>
      </div>
    </div>
  );
};
